// src/pages/ManageStoreEmbeds.js
import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebaseConfig';
import { useAuth } from '../AuthProvider';
import '../styles/BellaBizarreStyle.css';

const StoreManager = () => {
    const { user } = useAuth();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [embedHtml, setEmbedHtml] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [imageUrl, setImageUrl] = useState('');
    const [editingId, setEditingId] = useState(null); // NEW
    const [embeds, setEmbeds] = useState([]);
    const [message, setMessage] = useState('');
    const fileRef = useRef(null);

    useEffect(() => {
        const unsub = onSnapshot(collection(db, 'storeEmbeds'), snap =>
            setEmbeds(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
        return unsub;
    }, []);

    const handleImageUpload = async () => {
        if (!imageFile) return imageUrl || ''; // keep old image if not changing
        const path = `uploads/storeEmbeds/${Date.now()}_${imageFile.name}`;
        const storageRef = ref(storage, path);
        const uploadTask = uploadBytesResumable(storageRef, imageFile);
        return new Promise((resolve, reject) => {
            uploadTask.on(
                'state_changed',
                null,
                reject,
                async () => {
                    const url = await getDownloadURL(uploadTask.snapshot.ref);
                    resolve(url);
                }
            );
        });
    };

    const resetForm = () => {
        setTitle('');
        setDescription('');
        setEmbedHtml('');
        setImageFile(null);
        setImageUrl('');
        setEditingId(null);
        setMessage('');
        if (fileRef.current) fileRef.current.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        try {
            const uploadedImageUrl = await handleImageUpload();

            const data = {
                title,
                description,
                embedHtml,
                imageUrl: uploadedImageUrl,
                updatedAt: serverTimestamp()
            };

            if (editingId) {
                await updateDoc(doc(db, 'storeEmbeds', editingId), data);
                setMessage('Embed updated!');
            } else {
                await addDoc(collection(db, 'storeEmbeds'), {
                    ...data,
                    createdAt: serverTimestamp()
                });
                setMessage('Embed saved!');
            }

            resetForm();
        } catch (err) {
            console.error(err);
            setMessage('Error saving embed');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this embed?')) return;
        try {
            await deleteDoc(doc(db, 'storeEmbeds', id));
        } catch (err) {
            console.error(err);
        }
    };

    const handleEdit = (embed) => {
        setEditingId(embed.id);
        setTitle(embed.title);
        setDescription(embed.description);
        setEmbedHtml(embed.embedHtml);
        setImageUrl(embed.imageUrl);
        setImageFile(null);
        if (fileRef.current) fileRef.current.value = '';
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (!user) return <p>Please log in to manage embeds.</p>;

    return (
        <div className="event-uploader-container">
            <h2>{editingId ? 'Edit Store Embed' : 'Add Store Embed'}</h2>
            <form onSubmit={handleSubmit} className="event-uploader-form">
                <label>Title:
                    <input type="text" value={title} onChange={e => setTitle(e.target.value)} required />
                </label>
                <label>Description:
                    <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} required />
                </label>
                <label>Embed HTML:
                    <textarea
                        value={embedHtml}
                        onChange={e => setEmbedHtml(e.target.value)}
                        placeholder="Paste full <div>...</div> HTML from Square"
                        rows={6}
                        required
                    />
                </label>
                <label>Feature Image:
                    <input
                        type="file"
                        accept="image/*"
                        ref={fileRef}
                        onChange={e => setImageFile(e.target.files[0])}
                    />
                </label>
                {imageUrl && !imageFile && (
                    <img src={imageUrl} alt="Current" style={{ maxWidth: '200px', borderRadius: '8px' }} />
                )}
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                    <button type="submit" className="event-uploader-submit-button">
                        {editingId ? 'Update Embed' : 'Save Embed'}
                    </button>
                    {editingId && (
                        <button type="button" onClick={resetForm} className="event-uploader-reset-button">
                            Cancel Edit
                        </button>
                    )}
                </div>
            </form>
            {message && <p className="event-uploader-message">{message}</p>}

            <h2>Current Store Embeds</h2>
            {embeds.map(e => (
                <div className="store-item" key={e.id} style={{ marginBottom: '2rem' }}>
                    <h3>{e.title}</h3>
                    {e.imageUrl && (
                        <img src={e.imageUrl} alt={e.title} style={{ maxWidth: '300px', borderRadius: '8px' }} />
                    )}
                    <p>{e.description}</p>
                    <div dangerouslySetInnerHTML={{ __html: e.embedHtml }} />
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                        <button onClick={() => handleEdit(e)} className="event-uploader-edit-button">Edit</button>
                        <button onClick={() => handleDelete(e.id)} className="event-uploader-delete-button">Delete</button>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default StoreManager;
